import { motion } from 'framer-motion';
import { Phone, FileText, Target, Play, Rocket } from 'lucide-react';
import { fadeInUp, staggerContainer, viewportConfig } from '@/lib/animations';

const steps = [
  {
    icon: Phone,
    title: "Discovery Call",
    time: "Day 1",
    description: "30-minute strategy session to understand your goals, audience, and budget.",
  },
  {
    icon: FileText,
    title: "Custom Proposal",
    time: "Day 2-3",
    description: "Receive a tailored media plan with channel recommendations and transparent pricing.",
  },
  {
    icon: Target,
    title: "Audience & Geo Setup",
    time: "Day 4-5",
    description: "Define your target demographics and geographic zones, down to ZIP code precision.",
  },
  {
    icon: Play,
    title: "Creative Development",
    time: "Day 6-10",
    description: "Our team produces broadcast-ready ads or optimizes your existing assets.",
  },
  {
    icon: Rocket,
    title: "Campaign Launch",
    time: "Day 11+",
    description: "Go live on premium networks with real-time monitoring and optimization.",
  },
];

export function ProcessTimeline() {
  return (
    <section id="process" className="py-10 sm:py-12 md:py-14 lg:py-16 bg-navy-deep overflow-hidden">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={viewportConfig}
        variants={staggerContainer}
        className="container mx-auto px-5 sm:px-6"
      >
        {/* Section Header */}
        <motion.div variants={fadeInUp} className="text-center mb-10 md:mb-12">
          <span className="text-orange font-semibold text-xs sm:text-sm tracking-widest uppercase mb-2 block">
            Our Process
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-3">
            From First Call to First Impression.{' '}
            <span className="text-teal-light">In Days.</span>
          </h2>
          <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto">
            We've streamlined media buying so you can focus on your mission.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative max-w-4xl mx-auto">
          {/* Vertical line */}
          <div className="absolute left-5 lg:left-1/2 lg:-translate-x-px top-0 bottom-0 w-px bg-white/10 z-0">
            <motion.div
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.5, ease: [0.25, 0.1, 0.25, 1] }}
              className="w-full h-full bg-gradient-to-b from-orange via-teal to-teal-light origin-top"
            />
          </div>

          <div className="space-y-6 lg:space-y-4">
            {steps.map((step, index) => {
              const isLeft = index % 2 === 0;
              const Icon = step.icon;
              
              return (
                <motion.div
                  key={step.title}
                  variants={fadeInUp}
                  className="relative"
                >
                  {/* Mobile/Tablet */}
                  <div className="lg:hidden flex items-start gap-3">
                    <div className="relative flex-shrink-0">
                      <div className="absolute inset-0 w-10 h-10 rounded-full bg-orange/40 blur-md" />
                      <motion.div
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 + index * 0.08, duration: 0.25 }}
                        className="relative w-10 h-10 rounded-full bg-orange flex items-center justify-center z-10 shadow-lg"
                      >
                        <span className="text-white font-bold text-sm">{index + 1}</span>
                      </motion.div>
                    </div>
                    
                    <div className="flex-1 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-4 hover:border-orange/40 transition-all duration-300 group">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="p-1.5 bg-orange/20 rounded-md group-hover:bg-orange/30 transition-colors flex-shrink-0">
                            <Icon className="w-3.5 h-3.5 text-orange" />
                          </div>
                          <h3 className="text-white font-semibold text-sm">{step.title}</h3>
                        </div>
                        <span className="text-teal-light text-[10px] font-semibold bg-teal/10 px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                          {step.time}
                        </span>
                      </div>
                      <p className="text-white/60 text-xs leading-relaxed">{step.description}</p>
                    </div>
                  </div>

                  {/* Desktop - Zigzag */}
                  <div className="hidden lg:block">
                    <div className={`flex items-center gap-4 ${isLeft ? 'flex-row' : 'flex-row-reverse'}`}>
                      {/* Card */}
                      <div className={`flex-1 ${isLeft ? 'text-right' : 'text-left'}`}>
                        <div className={`inline-block bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:border-orange/40 transition-all duration-300 group max-w-md ${isLeft ? 'ml-auto' : 'mr-auto'}`}>
                          <div className={`flex items-center gap-2 mb-2 ${isLeft ? 'justify-end' : 'justify-start'}`}>
                            {isLeft && (
                              <span className="text-teal-light text-xs font-semibold bg-teal/10 px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                                {step.time}
                              </span>
                            )}
                            <div className="p-1.5 bg-orange/20 rounded-lg group-hover:bg-orange/30 transition-colors">
                              <Icon className="w-4 h-4 text-orange" />
                            </div>
                            {!isLeft && (
                              <span className="text-teal-light text-xs font-semibold bg-teal/10 px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                                {step.time}
                              </span>
                            )}
                          </div>
                          <h3 className="text-white font-semibold text-base mb-1">{step.title}</h3>
                          <p className="text-white/60 text-sm leading-relaxed">{step.description}</p>
                        </div>
                      </div>
                      
                      {/* Connector line */}
                      <div className={`w-8 h-px bg-gradient-to-r ${isLeft ? 'from-transparent to-orange' : 'from-orange to-transparent'}`} />
                      
                      {/* Step Number with glow */}
                      <div className="relative flex-shrink-0 z-10">
                        <div className="absolute -inset-2 rounded-full bg-orange/30 blur-lg" />
                        <motion.div
                          initial={{ scale: 0 }}
                          whileInView={{ scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.1 + index * 0.08, duration: 0.25 }}
                          className="relative w-10 h-10 rounded-full bg-orange flex items-center justify-center shadow-lg shadow-orange/40"
                        >
                          <span className="text-white font-bold text-base">{index + 1}</span>
                        </motion.div>
                      </div>
                      
                      {/* Spacer for the other side */}
                      <div className="flex-1" />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </section>
  );
}
